import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Lock, X, Network, Database } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useRun } from "@/context/RunContext";
import { LangflowViewer } from "@/features/langflow-viewer";
import { SourceDataViewer } from "@/components/SourceDataViewer";
import flowJson from "@/flows/invoice-reconciliation.json";

const CLOSE_TYPES = ["Month End", "Quarter End", "Year End"];

const NAV = [
  { key: "close_run", label: "Close Run", to: "/" },
  { key: "reconciliation", label: "Reconciliation", to: "/reconciliation" },
  { key: "corrective_actions", label: "Corrective Actions", to: "/corrective-actions" },
  { key: "close_readiness", label: "Close Readiness", to: "/close-readiness" },
];

const Dot = ({ status }) => {
  if (status === "done")
    return <span className="h-2.5 w-2.5 rounded-full bg-emerald-600" />;
  if (status === "active")
    return (
      <span className="relative flex h-2.5 w-2.5 items-center justify-center">
        <span className="absolute h-2.5 w-2.5 animate-ping rounded-full bg-blue-400 opacity-70" />
        <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />
      </span>
    );
  if (status === "locked")
    return <span className="h-2.5 w-2.5 rounded-full border border-zinc-300 bg-white" />;
  return <span className="h-2.5 w-2.5 rounded-full border border-zinc-400 bg-white" />;
};

export const AppShell = () => {
  const { navStatus, period, periods, periodId, setPeriodId } = useRun();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [showFlow, setShowFlow] = useState(false);
  const [showData, setShowData] = useState(false);

  return (
    <>
    <div className="flex min-h-screen bg-zinc-50">
      <aside
        data-testid="app-sidebar"
        className="sticky top-0 hidden h-screen w-72 shrink-0 flex-col justify-between border-r border-zinc-200 bg-white px-8 py-10 md:flex"
      >
        <div>
          <button
            data-testid="brand-home"
            onClick={() => navigate("/")}
            className="text-left"
          >
            <p className="font-heading text-lg font-medium tracking-tight text-zinc-900">
              Finance Close
            </p>
          </button>

          <nav className="mt-12" data-testid="stage-nav">
            {NAV.map((item, i) => {
              const onThisPage = pathname === item.to;
              const status =
                navStatus[item.key] === "locked" && onThisPage ? "active" : navStatus[item.key];
              const locked = status === "locked";
              return (
                <div key={item.key}>
                  <NavLink
                    to={item.to}
                    data-testid={`nav-${item.key}`}
                    data-status={status}
                    onClick={(e) => locked && e.preventDefault()}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 py-2 text-sm transition-colors duration-150 ${
                        locked
                          ? "cursor-not-allowed text-zinc-300"
                          : isActive
                            ? "text-zinc-900"
                            : "text-zinc-500 hover:text-zinc-900"
                      }`
                    }
                  >
                    <Dot status={status} />
                    <span className="font-medium">{item.label}</span>
                    {locked && <Lock className="h-3 w-3" strokeWidth={2} />}
                  </NavLink>
                  {i < NAV.length - 1 && (
                    <div className="ml-[5px] h-5 w-px bg-zinc-200" aria-hidden />
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        <div className="border-t border-zinc-200 pt-6">
          <button
            onClick={() => setShowFlow(true)}
            className="mb-3 flex w-full items-center justify-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-600 shadow-sm transition-colors hover:bg-zinc-50 hover:text-zinc-900"
          >
            <Network className="h-4 w-4" />
            View Flow
          </button>
          <button
            onClick={() => setShowData(true)}
            className="mb-4 flex w-full items-center justify-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-600 shadow-sm transition-colors hover:bg-zinc-50 hover:text-zinc-900"
          >
            <Database className="h-4 w-4" />
            View Data
          </button>
          <p className="text-[11px] uppercase tracking-wide text-zinc-400">Close period</p>
          <Select value={periodId} onValueChange={setPeriodId}>
            <SelectTrigger
              data-testid="period-select"
              className="mt-2 h-9 w-full rounded-md border-zinc-200 bg-white font-mono text-sm text-zinc-900 focus:ring-2 focus:ring-blue-600"
            >
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {CLOSE_TYPES.map((type) => {
                const group = periods.filter((p) => p.close_type === type);
                if (!group.length) return null;
                return (
                  <SelectGroup key={type}>
                    <SelectLabel className="text-[10px] uppercase tracking-wide text-zinc-400">
                      {type}
                    </SelectLabel>
                    {group.map((p) => (
                      <SelectItem
                        key={p.id}
                        value={p.id}
                        data-testid={`period-option-${p.id}`}
                        className="font-mono text-sm"
                      >
                        {p.label}
                        {!p.available && (
                          <span className="ml-2 text-[10px] uppercase text-zinc-400">no data</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                );
              })}
            </SelectContent>
          </Select>
          <p className="mt-3 font-mono text-[11px] text-zinc-400" data-testid="sidebar-period">
            {period ? `${period.close_type} · ${period.range}` : "—"}
          </p>
          <p className="mt-4 text-[11px] leading-relaxed text-zinc-400">
            Company Code 1000
          </p>
        </div>
      </aside>

      <main className="min-w-0 flex-1 px-6 py-12 md:px-16 md:py-16" data-testid="main-content">
        <Outlet />
      </main>

      {showFlow && (
        <div className="fixed inset-0 z-50 bg-white">
          <button
            onClick={() => setShowFlow(false)}
            className="absolute right-4 top-4 z-50 rounded-md border border-zinc-200 bg-white p-2 text-zinc-500 shadow-sm transition-colors hover:bg-zinc-50 hover:text-zinc-900"
            aria-label="Close flow viewer"
          >
            <X className="h-5 w-5" />
          </button>
          <LangflowViewer
            flow={flowJson}
            height="100vh"
            theme="light"
            showMinimap={true}
            showDetails={true}
            initialView="fit"
          />
        </div>
      )}

      {showData && <SourceDataViewer onClose={() => setShowData(false)} />}
    </div>
    </>
  );
};
